/* eslint-disable max-lines */
import { Button, UIText2 } from '@cian/ui-kit';
import * as React from 'react';

import { TagNew } from './TagNew';
import { clsx } from 'clsx';
import { trackGeoTagDeleted, trackClearButtonClick } from './trackings';
import { getValueFromComputedStyle } from './utils';

import style from './index.css';
import { parseTagValue } from '../../utils/parseTagValue';
import { FitInOneLine } from './fit_in_one_line';
import { ADORNMENTS } from './adornments';
import { TAdornment } from '../../types/tag';

interface ITagOption {
  value: string;
  label: string;
  leftAdornment?: TAdornment;
  rightAdornment?: TAdornment;
}

export interface ITagsProps {
  tags: ITagOption[];

  onTagDeleted(tag: ITagOption, index: number): void;
  onAllTagsDeleted(): void;
}

export interface ITagsOwnProps {
  isAdvancedFiltersOpened?: boolean;
}

export interface ITagsState {
  tagsMaxWidth: number;
  fitInOneLine: boolean;
}

const GEO_SPECIAL_TYPES_TAGS = [
  'geoLocation',
  'geoDistrict',
  'geoStreet',
  'geoUnderground',
  'geoRailway',
  'geoNewbuilding',
];

export type TTagsProps = ITagsProps & ITagsOwnProps;

export class Tags extends React.Component<TTagsProps, ITagsState> {
  public state = {
    tagsMaxWidth: 0,
    fitInOneLine: true,
  };

  private buttons: HTMLDivElement | null = null;
  private undergroundTravel: HTMLDivElement | null = null;
  private wrapper: HTMLDivElement | null = null;

  public componentDidMount() {
    this.updateTagsMaxWidth();
    window.addEventListener('resize', this.updateTagsMaxWidth);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.updateTagsMaxWidth);
  }

  public componentDidUpdate(prevProps: ITagsProps) {
    const clearButtonTagsLengthReached = prevProps.tags.length < 2 && this.props.tags.length >= 2;

    if (clearButtonTagsLengthReached) {
      this.updateTagsMaxWidth();
    }
  }

  public render() {
    const { tags } = this.props;
    const { fitInOneLine } = this.state;

    return (
      <div
        ref={wrapper => (this.wrapper = wrapper)}
        className={clsx(style['container'], fitInOneLine && style['container--line'])}
      >
        <FitInOneLine maxWidth={this.state.tagsMaxWidth} enabled={fitInOneLine} toggle={this.toggleFitInOneLine}>
          {tags.map((tag, index) => {
            return (
              <div className={style['tag']} key={index}>
                <TagNew key={index} onDeleteClick={() => this.onTagDeleted(tag, index)}>
                  {tag.leftAdornment ? this.renderAdornment(tag.leftAdornment) : null}
                  <UIText2>{tag.label}</UIText2>
                  {tag.rightAdornment ? this.renderAdornment(tag.rightAdornment) : null}
                </TagNew>
              </div>
            );
          })}
        </FitInOneLine>
        {tags.length >= 2 && (
          <div ref={buttons => (this.buttons = buttons)} className={style['buttons']}>
            <Button size="XS" theme="fill_secondary" onClick={this.handleAllTagsDeleted}>
              Очистить
            </Button>
          </div>
        )}
      </div>
    );
  }

  private renderAdornment = ({ type, props }: TAdornment) => {
    const Adornment = ADORNMENTS[type];

    return Adornment ? <Adornment {...props} /> : null;
  };

  private toggleFitInOneLine = () => {
    this.setState(prevState => ({ ...prevState, fitInOneLine: !prevState.fitInOneLine }));
  };

  private handleAllTagsDeleted = () => {
    trackClearButtonClick(this.props.isAdvancedFiltersOpened);
    this.props.onAllTagsDeleted();
  };

  private onTagDeleted = (tag: ITagOption, index: number) => {
    const [tagType] = parseTagValue(tag.value);

    /**
     * Если это гео тэг улицы, района, шоссе, региона или ЖД - отправляем аналитику
     */
    if (GEO_SPECIAL_TYPES_TAGS.includes(tagType)) {
      trackGeoTagDeleted(tagType);
    }

    this.props.onTagDeleted(tag, index);
  };

  private updateTagsMaxWidth = () => {
    if (!this.wrapper) {
      return;
    }

    let tagsMaxWidth = this.wrapper.getBoundingClientRect().width;

    if (this.undergroundTravel) {
      const computedStyle = getComputedStyle(this.undergroundTravel);
      tagsMaxWidth -= this.undergroundTravel.getBoundingClientRect().width;
      tagsMaxWidth -= getValueFromComputedStyle(computedStyle, 'marginRight');
    }

    if (this.buttons) {
      const computedStyle = getComputedStyle(this.buttons);
      tagsMaxWidth -= this.buttons.getBoundingClientRect().width;
      tagsMaxWidth -= getValueFromComputedStyle(computedStyle, 'marginLeft');
    }

    this.setState({
      tagsMaxWidth,
    });
  };
}
